import React, { useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import Masonry from "react-masonry-css";
import { getResponsiveImageAttributes } from "../../utils/imageUtils";

export interface GalleryImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  caption?: string;
  width?: number;
  height?: number;
}

interface ImageGalleryProps {
  images: GalleryImage[];
  onImageClick: (index: number) => void;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  onImageClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // Below md breakpoint (900px)
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  const handleImageLoad = (imageId: string) => {
    setLoadedImages((prev) => new Set(prev).add(imageId));
  };

  // Calculate aspect ratio for better layout
  const getImageAspectRatio = (image: GalleryImage): number => {
    if (image.width && image.height) {
      return image.height / image.width;
    }
    // Default aspect ratio if dimensions not available
    return 1.33; // 4:3
  };

  // Generate thumbnail URL from Cloudinary URL for mobile
  const getThumbnailUrl = (url: string): string => {
    // Use Cloudinary transformations to create smaller thumbnails
    // w_400 = width 400px, h_400 = height 400px, c_fill = crop fill
    if (url.includes("cloudinary.com")) {
      return url.replace(
        /\/upload\//,
        "/upload/w_400,h_400,c_fill,q_auto,f_auto/"
      );
    }
    return url;
  };

  const renderImage = (image: GalleryImage, index: number) => {
    const isLoaded = loadedImages.has(image.id);
    let aspectRatio = getImageAspectRatio(image);

    // Create mosaic pattern by varying aspect ratios
    // This creates visual variety: some tall, some wide, some square
    const sizePattern = index % 6;
    let adjustedAspectRatio = aspectRatio;

    if (sizePattern === 0) {
      // Tall portrait images
      adjustedAspectRatio = aspectRatio * 1.4;
    } else if (sizePattern === 1 || sizePattern === 2) {
      // Medium images - keep original ratio
      adjustedAspectRatio = aspectRatio * 1.1;
    } else if (sizePattern === 3) {
      // Wide landscape images
      adjustedAspectRatio = aspectRatio * 0.8;
    } else if (sizePattern === 4) {
      // Square-ish images
      adjustedAspectRatio = 1.0;
    } else {
      // Slightly tall images
      adjustedAspectRatio = aspectRatio * 1.2;
    }

    // Use thumbnail on mobile, full-size on desktop
    const thumbnailUrl = image.thumbnailUrl || getThumbnailUrl(image.url);
    const displayUrl = isMobile ? thumbnailUrl : image.url; // Thumbnails on mobile, full-size on desktop

    return (
      <Box
        key={image.id}
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        sx={{
          cursor: "pointer",
          position: "relative",
          width: "100%",
          mb: { xs: 1, sm: 2, md: 3 }, // Smaller spacing on mobile
          breakInside: "avoid",
          pageBreakInside: "avoid",
        }}
        onClick={() => onImageClick(index)}
      >
        {/* Image Container */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderRadius: { xs: 0, sm: 0 },
            // Vary container aspect ratios for mosaic effect
            paddingBottom: `${adjustedAspectRatio * 100}%`,
            "&:hover": {
              "& img": {
                transform: "scale(1.02)",
              },
            },
          }}
        >
          {/* Image - use thumbnail on mobile, full-size on desktop */}
          <Box
            component="img"
            alt={
              image.caption || `Photography by Mariah Blue - Image ${index + 1}`
            }
            {...(image.url.includes("cloudinary.com")
              ? getResponsiveImageAttributes(image.url, {
                  widths: [400, 800, 1200, 1600],
                  sizes: "(max-width: 600px) 100vw, (max-width: 960px) 50vw, 33vw",
                  defaultSize: isMobile ? 400 : 1200,
                })
              : { src: displayUrl })}
            loading={index < 6 ? "eager" : "lazy"}
            onLoad={() => handleImageLoad(image.id)}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "block",
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 0.4s ease, transform 0.4s ease",
              objectFit: "cover",
            }}
          />

          {/* Loading placeholder */}
          {!isLoaded && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                paddingBottom: `${adjustedAspectRatio * 100}%`,
                backgroundColor: "rgba(0, 0, 0, 0.03)",
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  // Empty state
  if (images.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 8, md: 12 },
          textAlign: "center",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            fontSize: { xs: "1rem", md: "1.125rem" },
            fontStyle: "italic",
          }}
        >
          No images in this gallery yet.
        </Typography>
      </Box>
    );
  }

  // Masonry breakpoints - thumbnail mosaic on mobile, full-size on desktop
  const breakpointColumnsObj = {
    default: 4, // 4 columns on large screens
    1400: 4, // Keep 4 columns on medium-large screens
    1200: 3, // 3 columns on medium screens
    900: 3, // 3 columns on smaller medium screens
    700: 2, // 2 columns on tablets
    500: 3, // 3 columns on larger mobile (thumbnail mosaic)
    400: 2, // 2 columns on medium mobile
    300: 2, // 2 columns on small mobile (thumbnail mosaic)
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: 1, sm: 2, md: 4, lg: 6 }, // Tighter padding on mobile for thumbnail mosaic
        py: { xs: 1, md: 4 }, // Less vertical padding on mobile
      }}
    >
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="masonry-grid"
        columnClassName="masonry-grid_column"
      >
        {images.map((image, index) => renderImage(image, index))}
      </Masonry>
    </Box>
  );
};
